import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import {
  ActionIcon,
  Divider,
  Modal,
  NumberInput,
  Select,
  Text,
  Tooltip,
} from '@mantine/core'
import { Plus, Trash2 } from 'lucide-react'

import { useMutation, useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import { isAdminOrOps } from 'src/lib/auth/permissions'
import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { useCurrentCompanyContext } from 'src/context/CurrentCompanyContext'
import {
  GET_COMPANY_VOE_OUTREACH_CONFIG,
  UPSERT_COMPANY_VOE_OUTREACH_CONFIG,
} from 'src/graphql/companyVoEOutreachConfig.graphql'
import { companyCanUseVoEVoiceAgent } from 'src/pages/ApplicantDetailsPage/tabs/VoETab/components/VoEVoiceAgentButton/VoEVoiceAgentButton'

import BackfillOutreachModal from './BackfillOutreachModal'
import FieldLabel from './FieldLabel'
import VoECallingSection from './voeCalling/VoECallingSection'
import type { VoECallingSectionRef } from './voeCalling/VoECallingSection'

interface StepDraft {
  id: string
  type: 'EMAIL' | 'CALL' | 'FAX'
  delayHours: number
}

interface AutomatedOutreachCardProps {
  companyId: string
  editing: boolean
}

export interface AutomatedOutreachCardRef {
  save: () => Promise<void>
  cancel: () => void
  loading: boolean
  canEdit: boolean
  /** Blocks the parent's Save while a field is invalid. */
  validationError: string | null
}

const DEFAULT_STEPS: Omit<StepDraft, 'id'>[] = [
  { type: 'EMAIL', delayHours: 0 },
  { type: 'EMAIL', delayHours: 24 },
  { type: 'EMAIL', delayHours: 24 },
]

const TRIGGER_OPTIONS = [
  { value: 'IMMEDIATELY', label: 'Immediately on verification creation' },
  { value: 'AFTER_MANUAL', label: 'After manual attempts' },
  { value: 'AUTOMATION_ONLY', label: 'Via automation rule only' },
]

const TRIGGER_DISPLAY: Record<string, string> = {
  IMMEDIATELY: 'Immediately on verification creation',
  AFTER_MANUAL: 'After manual attempts',
  AUTOMATION_ONLY: 'Via automation rule only',
}

let stepIdCounter = 0
const nextStepId = () => `step-${++stepIdCounter}`

function stepsFromConfig(
  steps: { type: string; delayHours: number }[]
): StepDraft[] {
  return steps.map((s) => ({
    id: nextStepId(),
    type: s.type as StepDraft['type'],
    delayHours: s.delayHours,
  }))
}

const AutomatedOutreachCard = forwardRef<
  AutomatedOutreachCardRef,
  AutomatedOutreachCardProps
>(({
  companyId,
  editing,
}, ref) => {
  const { currentUser } = useAuth()
  const isAdmin = isAdminOrOps(currentUser)
  const isRecruiter = currentUser?.roles?.includes('RECRUITER')
  const canEdit = isAdmin || isRecruiter

  const [enabled, setEnabled] = useState(false)
  const [businessDaysOnly, setBusinessDaysOnly] = useState(true)
  const [outreachTrigger, setOutreachTrigger] = useState<
    'IMMEDIATELY' | 'AFTER_MANUAL' | 'AUTOMATION_ONLY'
  >('IMMEDIATELY')
  const [requiredManualAttempts, setRequiredManualAttempts] = useState(1)
  const [skipCurrentEmployer, setSkipCurrentEmployer] = useState(false)
  const [steps, setSteps] = useState<StepDraft[]>([])
  const [backfillModalOpen, setBackfillModalOpen] = useState(false)

  const currentCompany = useCurrentCompanyContext()
  const showCalling = companyCanUseVoEVoiceAgent(currentCompany)
  const callingRef = useRef<VoECallingSectionRef>(null)
  // Automated Calling as shown in the section below, including unsaved edits.
  const [callingOn, setCallingOn] = useState(false)
  const emailLocked = showCalling && callingOn

  const { data, loading: queryLoading } = useQuery(
    GET_COMPANY_VOE_OUTREACH_CONFIG,
    { variables: { companyId } }
  )

  const config = data?.companyVoEOutreachConfig

  // Sync local state from query data when not editing
  useEffect(() => {
    if (!editing && config) {
      setEnabled(config.enabled)
      setBusinessDaysOnly(config.businessDaysOnly)
      setOutreachTrigger(config.outreachTrigger)
      setRequiredManualAttempts(config.requiredManualAttempts)
      setSkipCurrentEmployer(config.skipCurrentEmployer)
      setSteps(stepsFromConfig(config.steps))
    }
  }, [config, editing])

  const [upsertConfig, { loading: mutationLoading }] = useMutation(
    UPSERT_COMPANY_VOE_OUTREACH_CONFIG,
    {
      onCompleted: () => {
        toast('Automated outreach settings updated', 'success')
      },
      onError: (error) => {
        toast(`Failed to update settings: ${error.message}`, 'error')
      },
      refetchQueries: [
        {
          query: GET_COMPANY_VOE_OUTREACH_CONFIG,
          variables: { companyId },
        },
      ],
      awaitRefetchQueries: true,
    }
  )

  const loading = queryLoading || mutationLoading

  const handleCancel = () => {
    callingRef.current?.cancel()
    if (config) {
      setEnabled(config.enabled)
      setBusinessDaysOnly(config.businessDaysOnly)
      setOutreachTrigger(config.outreachTrigger)
      setRequiredManualAttempts(config.requiredManualAttempts)
      setSkipCurrentEmployer(config.skipCurrentEmployer)
      setSteps(stepsFromConfig(config.steps))
    } else {
      setEnabled(false)
      setBusinessDaysOnly(true)
      setOutreachTrigger('IMMEDIATELY')
      setRequiredManualAttempts(1)
      setSkipCurrentEmployer(false)
      setSteps([])
    }
  }

  // Call and fax steps don't reach an employer the agent found an email for.
  const hasEmailStep = steps.some((s) => s.type === 'EMAIL')
  const outreachError =
    emailLocked && (!enabled || !hasEmailStep)
      ? 'Automated outreach needs at least one email step while the VoE voice agent is on'
      : null

  // The voice agent hands off to email outreach, so it can't be on alone
  // (the server refuses it). Ask before turning email outreach on with it.
  const [confirmCallingOpen, setConfirmCallingOpen] = useState(false)
  const confirmCallingEnable = () => {
    if (enabled && hasEmailStep) return true
    setConfirmCallingOpen(true)
    return false
  }
  const turnOnCallingWithEmail = () => {
    setEnabled(true)
    if (!hasEmailStep) {
      setSteps((prev) => [
        ...prev,
        ...DEFAULT_STEPS.map((s) => ({ ...s, id: nextStepId() })),
      ])
    }
    callingRef.current?.enable()
    setConfirmCallingOpen(false)
  }

  const saveEmailOutreach = () =>
    upsertConfig({
      variables: {
        companyId,
        input: {
          enabled,
          businessDaysOnly,
          outreachTrigger,
          requiredManualAttempts,
          skipCurrentEmployer,
          steps: steps.map((s, i) => ({
            order: i + 1,
            type: s.type,
            delayHours: s.delayHours,
          })),
        },
      },
    })

  // One after the other: both write the same config row, and in parallel two
  // creates for a company without one would collide. The server keeps email
  // outreach on under the agent, so email goes first when the agent is on and
  // the agent goes first when it's being turned off.
  const handleSave = async () => {
    if (callingOn) {
      await saveEmailOutreach()
      await callingRef.current?.save()
    } else {
      await callingRef.current?.save()
      await saveEmailOutreach()
    }
  }

  useImperativeHandle(ref, () => ({
    save: handleSave,
    cancel: handleCancel,
    loading,
    canEdit,
    // A getter: the calling section re-renders on its own, so read it live.
    get validationError() {
      return outreachError ?? callingRef.current?.validationError ?? null
    },
  }))

  const addStep = () => {
    setSteps((prev) => [
      ...prev,
      { id: nextStepId(), type: 'EMAIL', delayHours: 0 },
    ])
  }

  const removeStep = (id: string) => {
    setSteps((prev) => prev.filter((s) => s.id !== id))
  }

  const updateStep = (id: string, patch: Partial<Omit<StepDraft, 'id'>>) => {
    setSteps((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...patch } : s))
    )
  }

  const formatDelay = (hours: number) => {
    if (hours === 0) return 'Immediate'
    const days = Math.floor(hours / 24)
    const remaining = hours % 24
    const parts: string[] = []
    if (days > 0) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`)
    if (remaining > 0)
      parts.push(`${remaining} ${remaining === 1 ? 'hour' : 'hours'}`)
    return parts.join(', ')
  }

  const formatStepDelay = (hours: number, isFirst: boolean) => {
    if (hours === 0)
      return isFirst ? 'Immediate' : 'Immediately after previous'
    const delay = formatDelay(hours)
    return isFirst
      ? `${delay} after verification is created or contact info is added`
      : `${delay} after previous`
  }

  const typeLabel = (type: string) => {
    if (type === 'EMAIL') return 'Email'
    if (type === 'CALL') return 'Call'
    if (type === 'FAX') return 'Fax'
    return type
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-3">
          <FieldLabel
            label="Automated Outreach"
            tooltip="When enabled, outreach emails are sent automatically according to the configured sequence when a new outgoing verification is created."
          />
          {editing ? (
            <Select
              data={[
                { value: 'enabled', label: 'Enabled' },
                { value: 'disabled', label: 'Disabled', disabled: emailLocked },
              ]}
              value={enabled ? 'enabled' : 'disabled'}
              onChange={(val) => {
                const nowEnabled = val === 'enabled'
                setEnabled(nowEnabled)
                if (nowEnabled && steps.length === 0) {
                  setSteps(DEFAULT_STEPS.map((s) => ({ ...s, id: nextStepId() })))
                }
              }}
              classNames={{
                label: 'text-sm font-medium text-doubleNickel-gray-700',
                input: 'text-sm',
              }}
            />
          ) : (
            <span className="text-sm text-doubleNickel-gray-700">
              {enabled ? 'Enabled' : 'Disabled'}
            </span>
          )}
        </div>

        <div className="col-span-3">
          <FieldLabel
            label="Business Days Scheduling"
            tooltip="When enabled, outreach is only scheduled on weekdays (Monday–Friday). Weekend dates are automatically pushed to the following Monday."
          />
          {editing ? (
            <Select
              data={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
              ]}
              value={businessDaysOnly ? 'yes' : 'no'}
              onChange={(val) => setBusinessDaysOnly(val === 'yes')}
              classNames={{
                label: 'text-sm font-medium text-doubleNickel-gray-700',
                input: 'text-sm',
              }}
            />
          ) : (
            <span className="text-sm text-doubleNickel-gray-700">
              {businessDaysOnly ? 'Yes' : 'No'}
            </span>
          )}
        </div>

        {enabled && (
          <div className="col-span-3">
            <FieldLabel
              label="Outreach Start Condition"
              tooltip={
                <>
                  <strong>Immediately on verification creation</strong>
                  <br />
                  Outreach starts as soon as an outgoing verification is created or when contact info is first added.
                  <br /><br />
                  <strong>After manual attempts</strong>
                  <br />
                  Waits until a recruiter logs a specified number of manual outreach attempts before starting the automated sequence.
                  <br /><br />
                  <strong>Via automation rule only</strong>
                  <br />
                  Outreach only starts when triggered by an Automation Rule — it will not run automatically on verification creation.
                </>
              }
            />
            {editing ? (
              <div className="flex flex-col gap-2">
                <Select
                  data={TRIGGER_OPTIONS}
                  value={outreachTrigger}
                  onChange={(val) =>
                    val &&
                    setOutreachTrigger(
                      val as 'IMMEDIATELY' | 'AFTER_MANUAL' | 'AUTOMATION_ONLY'
                    )
                  }
                  classNames={{
                    label: 'text-sm font-medium text-doubleNickel-gray-700',
                    input: 'text-sm',
                  }}
                />
                {outreachTrigger === 'AFTER_MANUAL' && (
                  <div className="flex items-center gap-2">
                    <Text size="sm" className="text-doubleNickel-gray-700">
                      Required attempts:
                    </Text>
                    <NumberInput
                      value={requiredManualAttempts}
                      onChange={(val) =>
                        setRequiredManualAttempts(
                          typeof val === 'number' && val >= 1 ? val : 1
                        )
                      }
                      min={1}
                      allowDecimal={false}
                      className="w-20"
                      classNames={{ input: 'text-sm' }}
                    />
                  </div>
                )}
                {outreachTrigger === 'AUTOMATION_ONLY' && (
                  <Text size="xs" className="text-doubleNickel-gray-500">
                    Configure the trigger in Automation Rules.
                  </Text>
                )}
              </div>
            ) : (
              <span className="text-sm text-doubleNickel-gray-700">
                {outreachTrigger === 'AFTER_MANUAL'
                  ? `${TRIGGER_DISPLAY[outreachTrigger]} (${requiredManualAttempts})`
                  : TRIGGER_DISPLAY[outreachTrigger] ?? outreachTrigger}
              </span>
            )}
          </div>
        )}

        {enabled && (
          <div className="col-span-3">
            <FieldLabel
              label="Backfill"
              tooltip="Start the outreach schedule for existing employment verifications that haven't had outreach triggered yet. You can choose which applicant stages to include."
            />
            <div className="mt-1 w-fit">
              <DNButton
                variant="outline"
                disabled={!config?.enabled || editing}
                onClick={() => setBackfillModalOpen(true)}
                className="text-xs"
              >
                Run
              </DNButton>
            </div>
          </div>
        )}

        <div className="col-span-3">
          <FieldLabel
            label="Skip Current Employers"
            tooltip="When enabled, automated emails and calls never go to an employer the applicant currently works for (no end date), even if the applicant consented. Recruiters can still reach out manually."
          />
          {editing ? (
            <Select
              data={[
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' },
              ]}
              value={skipCurrentEmployer ? 'yes' : 'no'}
              onChange={(val) => setSkipCurrentEmployer(val === 'yes')}
              classNames={{
                label: 'text-sm font-medium text-doubleNickel-gray-700',
                input: 'text-sm',
              }}
            />
          ) : (
            <span className="text-sm text-doubleNickel-gray-700">
              {skipCurrentEmployer ? 'Yes' : 'No'}
            </span>
          )}
        </div>
      </div>

      <Divider />

      {/* Outreach Schedule */}
      <div>
        <div className="mb-1 text-sm font-semibold text-doubleNickel-gray-900">
          Outreach Schedule
        </div>
        <div className="mb-2 text-xs text-doubleNickel-gray-500">
          The series of outreach attempts sent to the previous employer. Each
          step runs after the specified delay from the previous step.
        </div>
      </div>

      <div
        className={`transition-opacity duration-300 ${
          !enabled ? 'pointer-events-none opacity-40' : ''
        }`}
      >
        {steps.length === 0 ? (
          <div className="max-w-2xl">
            <div className="flex flex-col items-center gap-2 py-6 text-center text-sm text-doubleNickel-gray-400">
              No outreach steps configured.
            </div>
            {editing && (
              <button
                onClick={addStep}
                className="mt-2 w-full max-w-2xl rounded-md border border-dashed border-doubleNickel-gray-300 py-1.5 text-xs text-doubleNickel-brand-500 hover:border-doubleNickel-brand-500 hover:bg-doubleNickel-brand-25"
              >
                + Add step
              </button>
            )}
          </div>
        ) : (
          <div>
            <table className="w-full max-w-2xl">
              <thead>
                <tr className="border-b border-doubleNickel-gray-200">
                  <th className="w-12 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-doubleNickel-gray-500">
                    #
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-doubleNickel-gray-500">
                    Type
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-doubleNickel-gray-500">
                    Delay
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-doubleNickel-gray-500">
                    Timing
                  </th>
                  {editing && <th className="w-8" />}
                </tr>
              </thead>
              <tbody>
                {steps.map((step, idx) => (
                  <tr
                    key={step.id}
                    className="border-b border-doubleNickel-gray-100 hover:bg-doubleNickel-gray-50"
                  >
                    {editing ? (
                      <>
                        <td className="px-3 py-1.5 text-sm text-doubleNickel-gray-600">
                          {idx + 1}
                        </td>
                        <td className="w-28 px-3 py-1.5">
                          <Select
                            size="xs"
                            value={step.type}
                            onChange={(val) =>
                              val &&
                              updateStep(step.id, {
                                type: val as StepDraft['type'],
                              })
                            }
                            data={[
                              { value: 'EMAIL', label: 'Email' },
                              { value: 'CALL', label: 'Call' },
                              { value: 'FAX', label: 'Fax' },
                            ]}
                            classNames={{ input: 'text-xs' }}
                          />
                        </td>
                        <td className="w-28 px-3 py-1.5">
                          <input
                            type="number"
                            min={0}
                            value={step.delayHours}
                            onChange={(e) =>
                              updateStep(step.id, {
                                delayHours: parseInt(e.target.value) || 0,
                              })
                            }
                            className="w-full rounded border border-doubleNickel-gray-300 px-2 py-1 text-xs"
                          />
                        </td>
                        <td className="px-3 py-1.5 text-xs text-doubleNickel-gray-400">
                          {formatStepDelay(step.delayHours, idx === 0)}
                        </td>
                        <td className="px-3 py-1.5">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            size="sm"
                            onClick={() => removeStep(step.id)}
                          >
                            <Trash2 size={14} />
                          </ActionIcon>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-3 py-2 text-sm text-doubleNickel-gray-600">
                          {idx + 1}
                        </td>
                        <td className="px-3 py-2 text-sm">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                              step.type === 'EMAIL'
                                ? 'bg-amber-100 text-amber-700'
                                : step.type === 'CALL'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {typeLabel(step.type)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-sm text-doubleNickel-gray-900">
                          {step.delayHours === 0
                            ? 'Immediate'
                            : `${step.delayHours} hrs`}
                        </td>
                        <td className="px-3 py-2 text-sm text-doubleNickel-gray-500">
                          {formatStepDelay(step.delayHours, idx === 0)}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {editing && (
              <button
                onClick={addStep}
                className="mt-2 w-full max-w-2xl rounded-md border border-dashed border-doubleNickel-gray-300 py-1.5 text-xs text-doubleNickel-brand-500 hover:border-doubleNickel-brand-500 hover:bg-doubleNickel-brand-25"
              >
                + Add step
              </button>
            )}
          </div>
        )}
      </div>

      {showCalling && (
        <>
          <Divider />
          <VoECallingSection
            ref={callingRef}
            companyId={companyId}
            editing={editing}
            onEnabledChange={setCallingOn}
            confirmEnable={confirmCallingEnable}
          />
        </>
      )}

      <Modal
        opened={confirmCallingOpen}
        onClose={() => setConfirmCallingOpen(false)}
        title="The voice agent works with email outreach"
        centered
      >
        <div className="flex flex-col gap-4">
          <Text size="sm" className="text-doubleNickel-gray-600">
            The voice agent calls employers we don&apos;t have an email for.
            When it gets one, your email outreach schedule takes over.{' '}
            {hasEmailStep
              ? 'We’ll turn email outreach on with your current schedule.'
              : 'We’ll turn email outreach on with three emails: one right away, then one a day for two days.'}
          </Text>
          <div className="flex justify-end gap-2">
            <DNButton
              variant="outline"
              onClick={() => setConfirmCallingOpen(false)}
            >
              Cancel
            </DNButton>
            <DNButton onClick={turnOnCallingWithEmail}>Turn on both</DNButton>
          </div>
        </div>
      </Modal>

      <BackfillOutreachModal
        companyId={companyId}
        opened={backfillModalOpen}
        onClose={() => setBackfillModalOpen(false)}
      />
    </div>
  )
})

export default AutomatedOutreachCard
